import {
  AutopilotTaskDto,
  TaskCategoryTaskType,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client'
import {
  LaunchDetailsStatus,
  useStaffUpdateLaunchDateForCampaign,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
} from '@urbanx/agx.ui-components2'
import { useSelectedCampaign } from 'hooks'
import { JSX, ReactNode, useState } from 'react'
import { AwaitingAgentApprovalBlocker } from './AwaitingAgentApprovalBlocker'
import { NoLaunchDateBlocker } from './NoLaunchDateBlocker'
import { PendingTaskBlocker } from './PendingTaskBlocker'

type TaskBlockerProps = {
  task: AutopilotTaskDto
  children: ReactNode
}

export const TaskBlocker = ({
  task,
  children,
}: TaskBlockerProps): JSX.Element | null => {
  if (!task.category) return null

  // Handle pending tasks first - this takes priority over other blockers
  if (task.isPending) {
    return <PendingTaskBlocker task={task} />
  }

  const { campaign } = useSelectedCampaign()
  const { campaignId } = task
  const [isLaunchDateModalOpen, setIsLaunchDateModalOpen] = useState(false)
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()
  const { staffUpdateLaunchDateForCampaign } =
    useStaffUpdateLaunchDateForCampaign({
      onSuccess: () => {
        invalidateTasks(task.campaignId ?? '')
      },
      onError: () => {},
    })

    const onConfirmLaunchDate = () => {
      staffUpdateLaunchDateForCampaign?.({
        campaignId: campaignId,
        hasAgentApproval: false,
      })
    }
  switch (task.category.taskType) {
    
    case TaskCategoryTaskType.PreLaunch: {
      if (!campaign?.launchDateDetails?.launchDate) {
        return <NoLaunchDateBlocker task={task} />
      }
      return <>{children}</>
    }

    case TaskCategoryTaskType.Launch: {
      if (
        campaign?.launchDateDetails?.status ===
          LaunchDetailsStatus.ToBeConfirmed &&
        !campaign?.launchDateDetails.hasAgentApproval
      ) {
        return (
          <>
            <AwaitingAgentApprovalBlocker 
              onApprovalReceived={() => setIsLaunchDateModalOpen(true)} 
            />
            {setIsLaunchDateModalOpen && (
              <AgxModal
              titleIcon={
                <div className="modelTitleIconContainer">
                  <Icon_AlertCircleOutline type={'Danger'} height={40} width={40} />
                </div>
              }
              type="single"
              variant={'withoutDivider'}
              actionButtonContainerStyle={'withDivider'}
              actionButtons={
                <AgxSpacer direction='horizontal' gap={'spacing-md'} contentJustification="center" fill>
                  <AgxButton
                    id={'btnCancel'}
                    text={'Cancel'}
                    variant={'hollow'}
                    onClick={() => setIsLaunchDateModalOpen(false)}
                    stretch
                  />
                  <AgxButton
                    id={'btnConfirm'}
                    text={'Confirm'}
                    variant={'primary'}
                    onClick={onConfirmLaunchDate}
                    stretch
                  />
                </AgxSpacer>
              }
              content={
                <AgxSpacer direction="vertical" fill gap="sm" padding={{ bottom: 'xl', left: 'xl'}}>
                  <AgxTypography id={'launchDateModalTitle'} type={'body_ui_strong'} size={'medium'} color='var(--text-primary)'>Approval received</AgxTypography>
                  <AgxTypography id={'launchDateModalTitle'} type={'body_ui'} size={'small'} color='var(--text-secondary)'>Launch task will update to &apos;In progress&apos; once confirmed.</AgxTypography>
                </AgxSpacer>
              }
              isOpen={isLaunchDateModalOpen}
              setIsDialogOpen={setIsLaunchDateModalOpen}
            />
            )}
          </>
        )
      }

      return <>{children}</>
    }
    case TaskCategoryTaskType.Unconditional: {
      return <>{children}</>
    }
  }

  return <>{children}</>
}
